import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Loader2, Search, Filter, ChevronLeft, ChevronRight, Clock, CheckSquare, FileText, Bell, Edit2, Plus, List, Grid3x3, Eye, EyeOff, X } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import ProjectModal from './ProjectModal';
import AddUpdateModal from './AddUpdateModal';
import TaskModal from './TaskModal';
import AddHoursDialog from './AddHoursDialog';
import FlagIcon from './FlagIcon';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getDeadlineColor, formatDate } from '../utils/deadlineUtils';
import { formatEUR, getProjectValueEUR, fetchExchangeRates } from '../utils/currencyUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AllProjects = () => {
  const { token, user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [updateProject, setUpdateProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskProject, setTaskProject] = useState(null);
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const [hoursProject, setHoursProject] = useState(null);
  const [projectUpdates, setProjectUpdates] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [userAvatars, setUserAvatars] = useState({});
  const [rates, setRates] = useState(null);
  useEffect(() => { if (token) fetchExchangeRates(API, token).then(setRates); }, [token]);
  const [projectTasks, setProjectTasks] = useState({});
  const rowsPerPage = 25;
  
  // View mode state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('allProjectsViewMode') || 'table';
  });
  
  // For Kanban view - track hidden statuses
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenAllProjectStatuses');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Check if user is admin
  const isAdmin = hasRole('admin');

  // Save view preference
  useEffect(() => {
    localStorage.setItem('allProjectsViewMode', viewMode);
  }, [viewMode]);

  // Save hidden statuses preference
  useEffect(() => {
    localStorage.setItem('hiddenAllProjectStatuses', JSON.stringify(hiddenStatuses));
  }, [hiddenStatuses]);

  // Get last update date and aging color
  const getLastUpdateInfo = (projectId) => {
    const updates = projectUpdates[projectId] || [];
    if (updates.length === 0) return { date: 'Never', color: 'text-red-600', bgColor: 'bg-red-50' };
    
    const lastUpdate = updates[0]; // Assuming sorted by date desc
    const lastDate = new Date(lastUpdate.date);
    const now = new Date();
    const daysDiff = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
    
    let color, bgColor;
    if (daysDiff <= 7) {
      color = 'text-green-600';
      bgColor = 'bg-green-50';
    } else if (daysDiff <= 30) {
      color = 'text-yellow-600';
      bgColor = 'bg-yellow-50';
    } else {
      color = 'text-red-600';
      bgColor = 'bg-red-50';
    }
    
    return {
      date: lastDate.toLocaleDateString(),
      daysAgo: daysDiff,
      color,
      bgColor
    };
  };

  useEffect(() => {
    if (token) {
      fetchProjects();
      fetchStatusOptions();
    }
  }, [token]);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, selectedStatus]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch ALL projects for proper search and filtering
      const response = await axios.get(`${API}/projects`, { 
        headers,
        params: { 
          page: 1,
          rows_per_page: 1000  // Get all projects
        }
      });
      const projectsData = response.data.records || [];
      setProjects(projectsData);
      
      // Fetch updates for all projects
      fetchProjectUpdates(projectsData);
      
      // Fetch tasks count for all projects
      fetchProjectTasks(projectsData);
      
      // Fetch avatars for all users
      fetchUserAvatars(projectsData);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAvatars = async (projectsList) => {
    try {
      // Collect all unique user IDs from projects
      const userIds = new Set();
      projectsList.forEach(project => {
        // Owner
        if (project.field_237_raw?.[0]?.id) {
          userIds.add(project.field_237_raw[0].id);
        }
        // Assistants
        if (project.field_57_raw) {
          project.field_57_raw.forEach(assistant => {
            if (assistant.id) userIds.add(assistant.id);
          });
        }
      });

      console.log('🔍 AllProjects - Requesting avatars for user IDs:', Array.from(userIds));

      if (userIds.size === 0) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/users/avatars`,
        { user_ids: Array.from(userIds) },
        { headers }
      );
      
      console.log('✅ AllProjects - Received avatars:', response.data.avatars);
      console.log('📊 AllProjects - Avatar count:', Object.keys(response.data.avatars || {}).length, 'out of', userIds.size, 'requested');
      
      setUserAvatars(response.data.avatars || {});
    } catch (error) {
      console.error('Failed to fetch user avatars:', error);
    }
  };

  const fetchProjectUpdates = async (projectsList) => {
    // For now, use field_11_raw (last update date) from project itself
    // This is much faster than fetching updates for 200+ projects
    const updatesMap = {};
    projectsList.forEach(project => {
      if (project.field_11_raw) {
        // Mock update object with date
        updatesMap[project.id] = [{
          date: project.field_11_raw,
          text: 'Update',
          author: '',
          time: ''
        }];
      } else {
        updatesMap[project.id] = [];
      }
    });
    setProjectUpdates(updatesMap);
  };

  const fetchProjectTasks = async (projectsList) => {
    // Fetch task counts for all projects
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const tasksMap = {};
      
      // Fetch tasks in batches or use a summary endpoint if available
      const response = await axios.get(`${API}/tasks`, { 
        headers,
        params: { rows_per_page: 5000 } // Get all tasks
      });
      
      const allTasks = response.data?.records || [];
      
      // Group tasks by project
      allTasks.forEach(task => {
        const projectId = task.field_48_raw?.[0]?.id;
        if (projectId) {
          if (!tasksMap[projectId]) {
            tasksMap[projectId] = [];
          }
          tasksMap[projectId].push(task);
        }
      });
      
      setProjectTasks(tasksMap);
    } catch (error) {
      console.error('Failed to fetch project tasks:', error);
      setProjectTasks({});
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/reference-data/project-status`, { headers });
      const statusList = response.data?.records || response.data || [];
      
      // Sort by order field if it exists, otherwise by name
      const sorted = statusList.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.field_84_raw || '').localeCompare(b.field_84_raw || '');
      });
      
      setStatusOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch status options:', error);
      setStatusOptions([]);
    }
  };

  const handleAddHours = (project) => {
    // Open hours dialog instead of navigating
    setHoursProject(project);
    setShowHoursDialog(true);
  };

  const handleAddTask = (project) => {
    setTaskProject(project);
    setShowTaskModal(true);
  };

  const handleTaskSuccess = () => {
    // Refresh tasks after creating a new one
    fetchProjectTasks(projects);
  };

  const filterProjects = () => {
    let filtered = [...projects];

    if (searchTerm) {
      filtered = filtered.filter(project => {
        const name = project.field_5_raw?.toLowerCase() || '';
        const id = project.field_2_raw?.toLowerCase() || '';
        return name.includes(searchTerm.toLowerCase()) || id.includes(searchTerm.toLowerCase());
      });
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(project => {
        const status = project.field_85_raw?.[0]?.identifier || '';
        return status.toLowerCase().includes(selectedStatus.toLowerCase());
      });
    }

    // Sort by status order from settings (respects admin-configured order)
    const statusOrderMap = {};
    statusOptions.forEach((status, index) => {
      const statusName = (status.field_84_raw || status.field_84 || '').toLowerCase();
      statusOrderMap[statusName] = status.order !== undefined ? status.order : index;
    });

    filtered.sort((a, b) => {
      const statusA = (a.field_85_raw?.[0]?.identifier || '').toLowerCase();
      const statusB = (b.field_85_raw?.[0]?.identifier || '').toLowerCase();
      
      const orderA = statusOrderMap[statusA] !== undefined ? statusOrderMap[statusA] : 999;
      const orderB = statusOrderMap[statusB] !== undefined ? statusOrderMap[statusB] : 999;
      
      return orderA - orderB;
    });

    setFilteredProjects(filtered);
    // Reset to page 1 when filters change
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('ongoing')) return 'bg-green-100 text-green-800';
    if (statusLower.includes('lead')) return 'bg-blue-100 text-blue-800';
    if (statusLower.includes('submitted')) return 'bg-yellow-100 text-yellow-800';
    if (statusLower.includes('won') || statusLower.includes('awarded')) return 'bg-purple-100 text-purple-800';
    if (statusLower.includes('lost') || statusLower.includes('cancelled')) return 'bg-red-100 text-red-800';
    if (statusLower.includes('waiting')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  const groupByStatus = () => {
    const grouped = {};
    paginatedProjects.forEach(project => {
      const status = project.field_85_raw?.[0]?.identifier || 'No Status';
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(project);
    });
    return grouped;
  };

  // For Kanban view - filter out hidden statuses and maintain order
  const getVisibleGroupedProjects = () => {
    const grouped = {};
    
    // Initialize grouped object with statusOptions order
    statusOptions.forEach(status => {
      const statusName = status.field_84_raw;
      if (!hiddenStatuses.includes(statusName)) {
        grouped[statusName] = [];
      }
    });
    
    // Group projects into the ordered structure
    filteredProjects.forEach(project => {
      const status = project.field_85_raw?.[0]?.identifier || 'No Status';
      if (grouped[status]) {
        grouped[status].push(project);
      }
    });
    
    return grouped;
  };

  // Update project status
  const updateProjectStatus = async (projectId, newStatusId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/projects/${projectId}`, {
        field_85: [newStatusId]
      }, { headers });

      const updatedStatus = statusOptions.find(s => s.id === newStatusId);
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === projectId 
            ? { ...p, field_85_raw: [{ id: newStatusId, identifier: updatedStatus?.field_84_raw }] }
            : p
        )
      );
    } catch (error) {
      console.error('Failed to update project status:', error);
    }
  };

  // Handle drag end in Kanban view
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination || 
        (source.droppableId === destination.droppableId && 
         source.index === destination.index)) {
      return;
    }

    const project = projects.find(p => p.id === draggableId);
    const newStatus = destination.droppableId.replace('status-', '');
    const oldStatus = source.droppableId.replace('status-', '');

    console.log('🔄 Drag and drop:', {
      project: project?.field_5_raw,
      from: oldStatus,
      to: newStatus,
      availableStatuses: statusOptions.map(s => s.field_84_raw)
    });

    const newStatusOption = statusOptions.find(s => s.field_84_raw === newStatus);
    
    if (!newStatusOption) {
      console.error('❌ Status option not found for:', newStatus);
      console.error('Available status options:', statusOptions);
      return;
    }
    
    console.log('✅ Found status option:', newStatusOption);

    // Optimistically update both projects and filteredProjects
    const updateProject = (p) => {
      if (p.id === draggableId) {
        const updated = { 
          ...p, 
          field_85: [newStatusOption.id],
          field_85_raw: [{ id: newStatusOption.id, identifier: newStatus }] 
        };
        console.log('📝 Updating project in state:', {
          projectId: p.id,
          oldStatus: p.field_85_raw?.[0]?.identifier,
          newStatus: updated.field_85_raw?.[0]?.identifier
        });
        return updated;
      }
      return p;
    };

    // Update both states - create NEW arrays to ensure React detects the change
    setProjects(prevProjects => {
      const updated = [...prevProjects].map(updateProject);
      console.log('✅ Projects state updated', {
        totalProjects: updated.length,
        updatedProject: updated.find(p => p.id === draggableId)?.field_85_raw?.[0]?.identifier
      });
      return updated;
    });
    
    setFilteredProjects(prevFiltered => {
      const updated = [...prevFiltered].map(updateProject);
      const updatedProject = updated.find(p => p.id === draggableId);
      console.log('✅ FilteredProjects state updated', {
        totalFiltered: updated.length,
        updatedProjectId: draggableId,
        updatedProjectStatus: updatedProject?.field_85_raw?.[0]?.identifier,
        updatedProjectName: updatedProject?.field_5_raw
      });
      
      // Log the grouping to verify
      const grouped = {};
      updated.forEach(project => {
        const status = project.field_85_raw?.[0]?.identifier || 'No Status';
        if (!grouped[status]) grouped[status] = [];
        grouped[status].push(project.field_5_raw);
      });
      console.log('📊 Projects grouped by status:', grouped);
      
      return updated;
    });

    try {
      await axios.put(`${API}/projects/${draggableId}`, {
        field_85: [newStatusOption.id],
        field_85_raw: [{ 
          id: newStatusOption.id, 
          identifier: newStatus,
          field_84_raw: newStatus
        }]
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      console.log(`✅ Project ${draggableId} status updated on backend`);
      console.log('✅ UI already updated optimistically, no refetch needed');
      
      // Dispatch event to notify other components about status change
      window.dispatchEvent(new CustomEvent('projectUpdateCreated', {
        detail: { projectId: draggableId, type: 'status_change' }
      }));
    } catch (error) {
      console.error('Failed to update project status:', error);
      
      // Revert both states
      const revertProject = (p) => {
        if (p.id === draggableId) {
          return { 
            ...p, 
            field_85_raw: [{ identifier: oldStatus }] 
          };
        }
        return p;
      };
      
      setProjects(prevProjects => prevProjects.map(revertProject));
      setFilteredProjects(prevFiltered => prevFiltered.map(revertProject));
    }
  };

  const QuickActions = ({ project }) => (
    <div className="flex gap-1">
      {isAdmin && (
        <button
          onClick={() => {
            setEditingProject(project);
            setShowProjectModal(true);
          }}
          title="Edit Project (Admin)"
          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => {
          setUpdateProject(project);
          setShowAddUpdateModal(true);
        }}
        title="Add Update"
        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
      >
        <Plus className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleAddHours(project);
        }}
        title="Register Hours"
        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition cursor-pointer"
      >
        <Clock className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleAddTask(project);
        }}
        title="Add Task"
        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition cursor-pointer"
      >
        <CheckSquare className="w-4 h-4" />
      </button>
      <button
        title="Add Note"
        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition"
      >
        <FileText className="w-4 h-4" />
      </button>
      <button
        title="Add Reminder"
        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition"
      >
        <Bell className="w-4 h-4" />
      </button>
    </div>
  );

  const UserAvatars = ({ project }) => {
    const owner = project.field_237_raw?.[0];
    const assistants = project.field_57_raw || [];
    const allUsersWithDuplicates = owner ? [owner, ...assistants] : assistants;
    
    // Remove duplicate users by ID
    const allUsers = allUsersWithDuplicates.filter((user, index, self) =>
      index === self.findIndex(u => u.id === user.id)
    );

    const getAvatarUrl = (user) => {
      // Get avatar from fetched avatars map
      return userAvatars[user.id] || null;
    };

    return (
      <div className="flex -space-x-2">
        {allUsers.slice(0, 3).map((user, idx) => {
          const avatarUrl = getAvatarUrl(user);
          
          return (
            <div key={user.id} title={user.identifier} className="-ml-1 first:ml-0">
              <Avatar
                userId={user.id}
                userName={user.identifier}
                avatarUrl={avatarUrl}
                size="sm"
              />
            </div>
          );
        })}
        {allUsers.length > 3 && (
          <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 text-xs font-medium border-2 border-white dark:border-gray-700">
            +{allUsers.length - 3}
          </div>
        )}
      </div>
    );
  };

  // Calculate pagination for filtered projects
  const totalPages = Math.ceil(filteredProjects.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const groupedProjects = groupByStatus();

  return (
    <div className="space-y-6">
      {/* Project Modal */}
      {showProjectModal && (
        <ProjectModal
          project={editingProject}
          onClose={() => {
            setShowProjectModal(false);
            setEditingProject(null);
          }}
          onSave={fetchProjects}
        />
      )}

      {/* Add Update Modal */}
      {showAddUpdateModal && (
        <AddUpdateModal
          project={updateProject}
          onClose={() => {
            setShowAddUpdateModal(false);
            setUpdateProject(null);
          }}
          onSave={fetchProjects}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">All Projects</h1>
            <p className="text-gray-600 mt-1">
              {filteredProjects.length} projects {searchTerm || selectedStatus !== 'all' ? `(filtered from ${projects.length} total)` : ''}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Switcher */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
                title="Table View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  viewMode === 'kanban' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
                title="Kanban View"
              >
                <FolderKanban className="w-4 h-4" />
              </button>
            </div>

            {/* Create Project Button */}
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingProject(null);
                  setShowProjectModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-5 h-5" />
                Create Project
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-gray-900"
            >
              <option value="all" className="text-gray-900 bg-white">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status.id} value={status.field_84_raw.toLowerCase()} className="text-gray-900 bg-white">
                  {status.field_84_raw}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status visibility toggles - Show in both table and kanban views */}
        {statusOptions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-600 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Toggle columns:
            </span>
            {statusOptions.map(status => {
              const statusName = status.field_84_raw;
              const isHidden = hiddenStatuses.includes(statusName);
              return (
                <button
                  key={status.id}
                  onClick={() => {
                    setHiddenStatuses(prev => {
                      if (prev.includes(statusName)) {
                        return prev.filter(s => s !== statusName);
                      } else {
                        return [...prev, statusName];
                      }
                    });
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    isHidden 
                      ? 'bg-gray-200 text-gray-500 line-through' 
                      : getStatusColor(statusName)
                  }`}
                >
                  {isHidden ? <EyeOff className="w-3 h-3 inline mr-1" /> : <Eye className="w-3 h-3 inline mr-1" />}
                  {statusName}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Projects Display */}
      {viewMode === 'kanban' ? (
        /* KANBAN VIEW */
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Kanban header with view switcher - STICKY */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Kanban Board</h2>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('table')}
                    className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 transition"
                    title="Table View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-white text-gray-900 shadow-sm"
                    title="Kanban View (Current)"
                  >
                    <FolderKanban className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Scrollable Kanban columns */}
            <div className="p-6 overflow-x-auto">
              <div className="flex gap-4 pb-4 min-w-min">
                {Object.entries(getVisibleGroupedProjects()).map(([status, statusProjects]) => (
                  <div key={status} className="flex-shrink-0 w-80 bg-gray-50 rounded-lg">
                    <div className="p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">{status}</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 bg-white px-2 py-0.5 rounded-full">
                            {statusProjects.length}
                          </span>
                          <button
                            onClick={() => {
                              setHiddenStatuses(prev => [...prev, status]);
                            }}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title="Hide this column"
                          >
                            <X className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  <Droppable droppableId={`status-${status}`}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-3 space-y-3 min-h-[200px] max-h-[600px] overflow-y-auto ${
                          snapshot.isDraggingOver ? 'bg-blue-50' : ''
                        }`}
                      >
                        {statusProjects.map((project, index) => (
                          <Draggable
                            key={project.id}
                            draggableId={project.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-white border rounded-lg transition ${
                                  snapshot.isDragging
                                    ? 'border-blue-500 shadow-lg rotate-2'
                                    : 'border-gray-200 hover:shadow-md hover:border-blue-300'
                                }`}
                              >
                                <div className="flex items-start gap-2 p-3">
                                  <div 
                                    {...provided.dragHandleProps}
                                    className="text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0 pt-1"
                                    title="Drag to move"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                    </svg>
                                  </div>
                                  <FlagIcon countryCode={project.field_117_raw} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 mb-1">{project.field_2_raw || 'No ID'}</p>
                                    <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">
                                      {project.field_5_raw || 'Untitled Project'}
                                    </h4>
                                  </div>
                                </div>
                                <div className="px-3 pb-3">
                                  <div className="space-y-1 text-xs text-gray-600 mb-2">
                                    {project.field_10_raw && (
                                      <div className="flex justify-between">
                                        <span>Value:</span>
                                        <span className="font-medium">{getProjectValueEUR(project, rates) > 0 ? formatEUR(getProjectValueEUR(project, rates)) : '-'}</span>
                                      </div>
                                    )}
                                    {project.field_9_raw && (
                                      <div className="flex justify-between">
                                        <span>Deadline:</span>
                                        <span className={getDeadlineColor(project.field_9)}>{formatDate(project.field_9)}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                    <UserAvatars project={project} />
                                    <QuickActions project={project} />
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
              </div>
            </div>
          </div>
        </DragDropContext>
      ) : (
        /* TABLE VIEW - Projects grouped by status */
        Object.keys(groupedProjects).length > 0 ? (
        <>
          {Object.entries(groupedProjects).map(([status, statusProjects]) => (
            <div key={status} className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">{status}</h2>
                  <span className="text-sm text-gray-600">
                    {statusProjects.length} project{statusProjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Update</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statusProjects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <FlagIcon countryCode={project.field_117_raw} />
                            <div>
                              <p className="text-xs text-gray-500">{project.field_2_raw || 'No ID'}</p>
                              <button
                                onClick={() => {
                                  setEditingProject(project);
                                  setShowProjectModal(true);
                                }}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                              >
                                {project.field_5_raw || 'Untitled Project'}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getProjectValueEUR(project, rates) > 0 ? formatEUR(getProjectValueEUR(project, rates)) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={getDeadlineColor(project.field_9)}>
                            {formatDate(project.field_9)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const updateInfo = getLastUpdateInfo(project.id);
                            return (
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${updateInfo.bgColor} ${updateInfo.color}`}>
                                <Clock className="w-3 h-3" />
                                <span>{updateInfo.date}</span>
                                {updateInfo.daysAgo !== undefined && (
                                  <span className="text-[10px] opacity-75">({updateInfo.daysAgo}d)</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <UserAvatars project={project} />
                        </td>
                        <td className="px-6 py-4">
                          <QuickActions project={project} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-200">
                {statusProjects.map((project) => (
                  <div key={project.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <FlagIcon countryCode={project.field_117_raw} />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-1">{project.field_2_raw || 'No ID'}</p>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {project.field_5_raw || 'Untitled Project'}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 mb-2">
                      {project.field_10 && (
                        <p className="text-xs text-gray-600">Value: <span className="font-medium">{getProjectValueEUR(project, rates) > 0 ? formatEUR(getProjectValueEUR(project, rates)) : '-'}</span></p>
                      )}
                      {project.field_9 && (
                        <p className="text-xs text-gray-600">Deadline: <span className={`font-medium ${getDeadlineColor(project.field_9)}`}>{formatDate(project.field_9)}</span></p>
                      )}
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <UserAvatars project={project} />
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <QuickActions project={project} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow px-6 py-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FolderKanban className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        )
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setTaskProject(null);
          }}
          task={null}
          onSuccess={() => {
            handleTaskSuccess();
            setShowTaskModal(false);
            setTaskProject(null);
          }}
          preSelectedProject={taskProject}
        />
      )}

      {/* Add Hours Dialog */}
      {showHoursDialog && (
        <AddHoursDialog
          isOpen={showHoursDialog}
          onClose={() => {
            setShowHoursDialog(false);
            setHoursProject(null);
          }}
          project={hoursProject}
          onSuccess={() => {
            setShowHoursDialog(false);
            setHoursProject(null);
          }}
          onCreateTask={(project) => {
            setTaskProject(project);
            setShowTaskModal(true);
          }}
        />
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setTaskProject(null);
          }}
          task={null}
          onSuccess={() => {
            setShowTaskModal(false);
            setTaskProject(null);
            fetchProjects(); // Refresh projects after adding task
          }}
          preSelectedProject={taskProject}
        />
      )}
    </div>
  );
};

export default AllProjects;
